import { supportsNormalizedTemplate } from '../helpers/patterns/patternCoordinates';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Alert, Card, message, Button, Row, Col, List, Empty, Spin, Space, Select, Typography, Modal } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getUserRoleCode, hasPermission } from '../helpers/accessControl';
import '../style/Patterns.css';
import AreaMap from '../components/Map/AreaMap';
import Map, { Layer, Marker, Source } from 'react-map-gl';
import type { MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoiZS1uYW5vIiwiYSI6ImNtamM0cjZzcjBtMW8zZnNiZ2I4NmljYTIifQ.MP4bn1hVGwhOXujMC4wjUw';
const { Title } = Typography;

interface PatternRow {
    archivedAt?: string | null;
    id: number;
    name: string;
    patternType: string;
    coordSystem: string;
    numPoints?: number;
    isGlobal: boolean;
    area?: { id: number; name: string } | null;
    areaType?: { id: number; name: string } | null;
    company?: { id: number; name: string } | null;
    createdAt?: string;
}

interface AreaTypeOption {
    id: number;
    name: string;
    maxVertices?: number | null;
    templateImage?: string | null;
    patternCoordinateMode?: 'gps' | 'normalized' | null;
}

interface AreaOption {
    id: number;
    name: string;
    code?: string;
    areaTypeId?: number | null;
}

interface PointRow {
    pointIndex: number;
    xLocal: number;
    yLocal: number;
    label?: string;
}

const Patterns: React.FC = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [creationMode, setCreationMode] = useState<'gps' | 'template' | null>(null);
    const [showArchived, setShowArchived] = useState(false);
    const [patterns, setPatterns] = useState<PatternRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [patternPoints, setPatternPoints] = useState<PointRow[]>([]);
    const [areaTypes, setAreaTypes] = useState<AreaTypeOption[]>([]);
    const [areas, setAreas] = useState<AreaOption[]>([]);
    const [selectedAreaTypeId, setSelectedAreaTypeId] = useState<number | null>(null);
    const [selectedPatternId, setSelectedPatternId] = useState<number | null>(null);
    const [templateUrl, setTemplateUrl] = useState<string | null>(null);
    const templateObjectUrl = useRef<string | null>(null);
    const [templateAspectPct, setTemplateAspectPct] = useState<number>(60);
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const [loadingPatterns, setLoadingPatterns] = useState(false);
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
    const [selectedAreaGeom, setSelectedAreaGeom] = useState<any>(null);
    const [areaSpecificPatterns, setAreaSpecificPatterns] = useState<PatternRow[]>([]);
    const [loadingAreaPatterns, setLoadingAreaPatterns] = useState(false);
    const [selectedAreaPatternId, setSelectedAreaPatternId] = useState<number | null>(null);
    const [selectedAreaPatternPoints, setSelectedAreaPatternPoints] = useState<any[]>([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [imageBounds, setImageBounds] = useState({ left: 0, top: 0, width: 0, height: 0 });
    const previewRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const mapRef = useRef<MapRef>(null);

    const companyId = currentUser?.currentCompanyId || currentUser?.companyId || null;
    const isMaster = currentUser?.isMaster === true;
    const userRoleCode = getUserRoleCode(currentUser);
    const canEditPattern = hasPermission(isMaster, userRoleCode, 'edit_pattern');
    const canDeletePattern = hasPermission(isMaster, userRoleCode, 'delete_pattern');

    // Calculate image bounds for preview
    useEffect(() => {
        if (!templateUrl || !previewRef.current) return;

        const img = new Image();
        imageRef.current = img;
        
        const calculateBounds = () => {
            const container = previewRef.current;
            const image = imageRef.current;
            if (!container || !image || !image.complete) return;

            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;
            const imageAspect = image.naturalWidth / image.naturalHeight;
            const containerAspect = containerWidth / containerHeight;

            let displayWidth, displayHeight, offsetX, offsetY;

            if (containerAspect > imageAspect) {
                displayHeight = containerHeight;
                displayWidth = displayHeight * imageAspect;
                offsetX = (containerWidth - displayWidth) / 2;
                offsetY = 0;
            } else {
                displayWidth = containerWidth;
                displayHeight = displayWidth / imageAspect;
                offsetX = 0;
                offsetY = (containerHeight - displayHeight) / 2;
            }

            console.log('[Patterns Preview] Image bounds:', { offsetX, offsetY, displayWidth, displayHeight, containerWidth, containerHeight, imageAspect });

            setImageBounds({
                left: offsetX,
                top: offsetY,
                width: displayWidth,
                height: displayHeight,
            });
        };

        img.onload = () => {
            setTimeout(calculateBounds, 100);
        };
        img.src = templateUrl;

        // Add resize observer to recalculate bounds on container resize
        const resizeObserver = new ResizeObserver(() => {
            calculateBounds();
        });

        if (previewRef.current) {
            resizeObserver.observe(previewRef.current);
        }

        return () => {
            resizeObserver.disconnect();
            imageRef.current = null;
        };
    }, [templateUrl, selectedPatternId]);

    // Refresh patterns when returning to this view
    useEffect(() => {
        const handleFocus = () => {
            setRefreshKey(prev => prev + 1);
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, []);

    useEffect(() => {
        const loadRefs = async () => {
            try {
                const [typesRes, areasRes] = await Promise.all([
                    axios.get(ENDPOINTS.AREA_TYPES_V2, { withCredentials: true }),
                    axios.get(ENDPOINTS.AREAS_V2, { withCredentials: true, params: { include_archived: showArchived ? 'true' : undefined } }),
                ]);
                setAreaTypes((typesRes.data?.data || typesRes.data || []) as AreaTypeOption[]);
                const mappedAreas = (areasRes.data?.data || areasRes.data || []).map((a: any) => ({
                    id: a.id,
                    name: a.name,
                    code: a.code,
                    areaTypeId: a.areaType?.id || null,
                })) as AreaOption[];
                setAreas(mappedAreas);
                const firstType = (typesRes.data?.data || typesRes.data || [])[0];
                if (firstType?.id) {
                    setSelectedAreaTypeId(previous => previous ?? firstType.id);
                }
            } catch (err) {
                console.warn('Failed to load area types/areas for patterns', err);
            }
        };
        loadRefs();
    }, [showArchived, refreshKey]);

    useEffect(() => {
        const loadPatterns = async () => {
            if (!companyId && !currentUser?.role) return;
            if (!selectedAreaTypeId) return;
            setLoadingPatterns(true);
            try {
                const res = await axios.get(ENDPOINTS.PATTERNS, {
                    withCredentials: true,
                    params: { area_type_id: selectedAreaTypeId, limit: 200, include_archived: showArchived ? 'true' : undefined },
                });
                const data = res.data?.data || res.data || [];
                console.log('[Patterns] Loaded patterns:', data);
                setPatterns(data);
                if (data.length > 0) {
                    setSelectedPatternId(data[0].id);
                } else {
                    setSelectedPatternId(null);
                    setPatternPoints([]);
                }
            } catch (err) {
                console.error('Failed to load patterns', err);
                message.error('Unable to load patterns');
            } finally {
                setLoadingPatterns(false);
            }
        };
        loadPatterns();
    }, [companyId, currentUser?.role, selectedAreaTypeId, refreshKey, showArchived]);

    useEffect(() => {
        const loadTemplate = async () => {
            if (!selectedAreaTypeId) {
                setTemplateUrl(null);
                return;
            }
            setLoadingTemplate(true);
            try {
                const res = await axios.get(`${ENDPOINTS.AREA_TYPES_V2}/${selectedAreaTypeId}/template-image/download?t=${Date.now()}`, {
                    withCredentials: true,
                    responseType: 'blob',
                });
                if (templateObjectUrl.current) URL.revokeObjectURL(templateObjectUrl.current);
                const url = URL.createObjectURL(res.data as Blob);
                templateObjectUrl.current = url;
                setTemplateUrl(url);

                // Probe image dimensions to set aspect ratio
                const img = new Image();
                img.onload = () => {
                    const aspectPct = img.width ? (img.height / img.width) * 100 : 60;
                    setTemplateAspectPct(aspectPct);
                };
                img.onerror = () => setTemplateAspectPct(60);
                img.src = url;
            } catch (err) {
                console.warn('Failed to load template image', err);
                if (templateObjectUrl.current) URL.revokeObjectURL(templateObjectUrl.current);
                templateObjectUrl.current = null;
                setTemplateUrl(null);
                setTemplateAspectPct(60);
            } finally {
                setLoadingTemplate(false);
            }
        };
        loadTemplate();
    }, [selectedAreaTypeId]);

    useEffect(() => {
        const loadPoints = async () => {
            if (!selectedPatternId) {
                console.log('[Patterns] No pattern selected, clearing points');
                setPatternPoints([]);
                return;
            }
            console.log('[Patterns] Loading points for pattern:', selectedPatternId);
            try {
                const res = await axios.get(`${ENDPOINTS.PATTERNS}/${selectedPatternId}/points`, { withCredentials: true });
                const data = res.data?.data || res.data || [];
                console.log('[Patterns] Loaded points:', data);
                setPatternPoints(
                    data
                        .slice()
                        .sort((a: any, b: any) => (a.pointIndex || 0) - (b.pointIndex || 0))
                        .map((p: any) => ({
                            pointIndex: p.pointIndex,
                            xLocal: p.xLocal ?? 0,
                            yLocal: p.yLocal ?? 0,
                            label: p.label || '',
                        })),
                );
            } catch (err) {
                console.error('Failed to load pattern points', err);
                setPatternPoints([]);
            }
        };
        loadPoints();
    }, [selectedPatternId, refreshKey]);

    useEffect(() => {
        return () => {
            if (templateObjectUrl.current) URL.revokeObjectURL(templateObjectUrl.current);
        };
    }, []);

    // Clear pattern state when area changes
    useEffect(() => {
        // Immediately clear pattern-related state when area changes
        setSelectedAreaPatternId(null);
        setSelectedAreaPatternPoints([]);
    }, [selectedAreaId]);

    // Fetch area detail when area selected in fallback mode
    useEffect(() => {
        const fetchAreaDetail = async () => {
            if (!selectedAreaId) {
                setSelectedAreaGeom(null);
                return;
            }
            try {
                const res = await axios.get(ENDPOINTS.AREA_DETAIL(selectedAreaId), { withCredentials: true });
                const payload = res.data?.data || res.data;
                setSelectedAreaGeom(payload?.geom || null);
            } catch (err) {
                console.warn('Failed to load area detail', err);
                setSelectedAreaGeom(null);
            }
        };
        fetchAreaDetail();
    }, [selectedAreaId]);

    // Fetch patterns for selected area in fallback mode
    useEffect(() => {
        const fetchAreaPatterns = async () => {
            if (!selectedAreaId) {
                setAreaSpecificPatterns([]);
                setSelectedAreaPatternId(null);
                return;
            }
            setLoadingAreaPatterns(true);
            try {
                const res = await axios.get(ENDPOINTS.PATTERNS, {
                    withCredentials: true,
                    params: { area_id: selectedAreaId, limit: 200, include_archived: showArchived ? 'true' : undefined },
                });
                const data = res.data?.data || res.data || [];
                setAreaSpecificPatterns(data);
                if (!data.length) { setSelectedAreaPatternId(null); setSelectedAreaPatternPoints([]); }
                // Auto-select first pattern
                if (data.length > 0 && !data.some((p: PatternRow) => p.id === selectedAreaPatternId)) {
                    setSelectedAreaPatternId(data[0].id);
                }
            } catch (err) {
                console.warn('Failed to load area-specific patterns', err);
                setAreaSpecificPatterns([]);
            } finally {
                setLoadingAreaPatterns(false);
            }
        };
        fetchAreaPatterns();
    }, [selectedAreaId, refreshKey, showArchived]);

    // Fetch points for selected pattern in fallback mode
    useEffect(() => {
        const fetchPatternPoints = async () => {
            if (!selectedAreaPatternId) {
                setSelectedAreaPatternPoints([]);
                return;
            }
            try {
                const res = await axios.get(`${ENDPOINTS.PATTERNS}/${selectedAreaPatternId}/points`, { 
                    withCredentials: true 
                });
                const data = res.data?.data || res.data || [];
                console.log('[Patterns] Loaded GPS points for pattern:', selectedAreaPatternId, data);
                setSelectedAreaPatternPoints(data);
            } catch (err) {
                console.warn('Failed to load pattern points for map', err);
                setSelectedAreaPatternPoints([]);
            }
        };
        fetchPatternPoints();
    }, [selectedAreaPatternId]);

    const selectedAreaType = areaTypes.find((t) => t.id === selectedAreaTypeId);
    const canUseTemplate = supportsNormalizedTemplate(selectedAreaType);
    const shouldShowFallback = creationMode === 'gps' || !canUseTemplate;
    const areasForSelectedType = areas.filter((a) => a.areaTypeId === selectedAreaTypeId);

    useEffect(() => {
        const mode = shouldShowFallback ? 'gps' : 'template';
        window.dispatchEvent(new CustomEvent('oscar:patterns-tour-mode', { detail: { mode } }));
    }, [shouldShowFallback]);

    // Auto-select first area when areas list changes in fallback mode
    useEffect(() => {
        if (shouldShowFallback && !areasForSelectedType.some(area => area.id === selectedAreaId)) {
            setSelectedAreaId(areasForSelectedType[0]?.id ?? null);
        }
    }, [shouldShowFallback, areasForSelectedType, selectedAreaId]);

    const handleCreatePattern = () => {
        if (shouldShowFallback) {
            // Navigate to area-specific pattern creator
            if (selectedAreaId) {
                navigate(`/patterns/create/area?areaId=${selectedAreaId}`);
            } else {
                message.warning('Please select an area first');
            }
        } else {
            // Navigate to template-based pattern creator
            if (selectedAreaTypeId) {
                navigate(`/patterns/create/template?areaTypeId=${selectedAreaTypeId}`);
            } else {
                message.warning('Please select an area type first');
            }
        }
    };

    const handleEditPattern = (pattern: PatternRow) => {
        const areaId = pattern.area?.id ?? null;
        const areaTypeId = pattern.areaType?.id ?? selectedAreaTypeId ?? null;
        const route = areaId
            ? `/patterns/create/area?areaId=${areaId}&patternId=${pattern.id}`
            : areaTypeId
                ? `/patterns/create/template?areaTypeId=${areaTypeId}&patternId=${pattern.id}`
                : null;

        if (!route) {
            message.warning('Pattern is missing area context');
            return;
        }

        Modal.confirm({
            title: 'Edit pattern points?',
            content: (
                <div>
                    <Alert
                        type="info"
                        showIcon
                        message="Moving points is safe"
                        description="Repositioning points updates the pattern without removing measurements."
                    />
                    <Alert
                        type="warning"
                        showIcon
                        message="Deleting points has consequences"
                        description="Removing points will clear links to measurements and delete related reports, conversations, and analysis data."
                        style={{ marginTop: 8 }}
                    />
                </div>
            ),
            okText: 'Continue',
            cancelText: 'Cancel',
            onOk: () => navigate(route),
        });
    };

    const handleArchivePattern = (pattern: PatternRow) => {
        const archived = !pattern.archivedAt;
        Modal.confirm({
            title: archived ? `Archive "${pattern.name}"?` : `Restore "${pattern.name}"?`,
            content: 'Archived patterns are hidden and excluded from Lightbox downloads. Measurement and point links are preserved.',
            onOk: async () => {
                try {
                    await axios.post(`${ENDPOINTS.PATTERNS}/${pattern.id}/archive`, { archived }, { withCredentials: true });
                    setSelectedPatternId(null);
                    setSelectedAreaPatternId(null);
                    setPatternPoints([]);
                    setSelectedAreaPatternPoints([]);
                    setRefreshKey(key => key + 1);
                    message.success(archived ? 'Pattern archived' : 'Pattern restored');
                } catch (err: any) {
                    message.error(err?.response?.data?.message || 'Could not update archive status');
                }
            },
        });

    };

    const handleDeletePatternInline = (pattern: PatternRow) => {
        Modal.confirm({
            title: 'Delete pattern',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#cf1322' }}>
                        This action is destructive and permanent.
                    </div>
                    <Alert
                        type="error"
                        showIcon
                        message="Pattern points and downstream data will be deleted"
                        description="Reports, conversations, analysis summaries, learning data, and mission points linked to these points will be removed."
                    />
                    <Alert
                        type="warning"
                        showIcon
                        message="Measurements will be kept but unlinked"
                        description="Existing measurements will remain but their point link will be cleared."
                        style={{ marginTop: 8 }}
                    />
                    <div style={{ marginTop: 12, fontWeight: 600 }}>
                        This cannot be undone.
                    </div>
                </div>
            ),
            okText: 'Delete',
            okType: 'danger',
            onOk: async () => {
                try {
                    await axios.delete(`${ENDPOINTS.PATTERNS}/${pattern.id}`, { withCredentials: true });
                    message.success('Pattern deleted');
                    if (selectedPatternId === pattern.id) {
                        setSelectedPatternId(null);
                        setPatternPoints([]);
                    }
                    if (selectedAreaPatternId === pattern.id) {
                        setSelectedAreaPatternId(null);
                        setSelectedAreaPatternPoints([]);
                    }
                    setRefreshKey((prev) => prev + 1);
                } catch (err: any) {
                    const status = err?.response?.status;
                    if (status === 409) {
                        message.error('Cannot delete a pattern that is in use');
                        return;
                    }
                    message.error(err?.response?.data?.message || 'Failed to delete pattern');
                }
            },
        });
    };

    const renderPatternPreview = () => {
        if (!templateUrl) {
            return (
                <div className="pattern-preview-empty">
                    {loadingTemplate ? <Spin /> : <Empty description="No template image" />}
                </div>
            );
        }
        const hasPoints = patternPoints.length > 0;
        return (
            <div 
                ref={previewRef}
                style={{ 
                    backgroundImage: templateUrl ? `url(${templateUrl})` : undefined,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundColor: '#fafafa',
                    position: 'relative',
                    width: '100%',
                    height: '500px',
                    border: '2px solid #d9d9d9',
                    borderRadius: 8,
                }}
            >
                    {hasPoints && imageBounds.width > 0 && patternPoints.map((p) => (
                        <div
                            key={p.pointIndex}
                            style={{
                                position: 'absolute',
                                left: `${imageBounds.left + p.xLocal * imageBounds.width}px`,
                                top: `${imageBounds.top + (1 - p.yLocal) * imageBounds.height}px`,
                                transform: 'translate(-50%, -50%)',
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                background: '#fa8c16',
                                border: '2px solid white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 10,
                                fontWeight: 'bold',
                                color: 'white',
                                zIndex: 10,
                            }}
                        >
                            {p.pointIndex}
                        </div>
                ))}
            </div>
        );
    };

    const renderFallbackView = () => {
        return (
            <Row gutter={[16, 16]}>
                <Col xs={24} md={10}>
                    <div className="patterns-fallback-areas-panel" style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8, fontWeight: 600 }}>Areas</div>
                        <List
                            dataSource={areasForSelectedType}
                            locale={{ emptyText: <Empty description="No areas of this type" /> }}
                            className="patterns-scroll-list patterns-fallback-areas-list"
                            renderItem={(area) => (
                                <List.Item
                                    className="patterns-fallback-area-item"
                                    onClick={() => setSelectedAreaId(area.id)}
                                    style={{
                                        cursor: 'pointer',
                                        background: selectedAreaId === area.id ? '#e6f4ff' : undefined,
                                        borderRadius: 6,
                                        padding: '8px 12px',
                                    }}
                                >
                                    <List.Item.Meta
                                        title={area.name}
                                        description={area.code || '—'}
                                    />
                                </List.Item>
                            )}
                        />
                    </div>
                    <div className="patterns-fallback-patterns-panel">
                        <div style={{ marginBottom: 8, fontWeight: 600 }}>Patterns for Selected Area</div>
                        <List
                            loading={loadingAreaPatterns}
                            dataSource={areaSpecificPatterns}
                            locale={{ emptyText: <Empty description={selectedAreaId ? 'No patterns for this area' : 'Select an area'} /> }}
                            className="patterns-scroll-list patterns-fallback-patterns-list"
                            renderItem={(item) => (
                                <List.Item
                                    className="patterns-fallback-pattern-item"
                                    onClick={() => setSelectedAreaPatternId(item.id)}
                                    actions={[
                                        canEditPattern ? (
                                            <Button key="archive" size="small" type="link" onClick={e => { e.stopPropagation(); handleArchivePattern(item); }}>
                                                {item.archivedAt ? 'Restore' : 'Archive'}
                                            </Button>
                                        ) : null,
                                        canEditPattern ? (
                                            <Button
                                                key="edit"
                                                size="small"
                                                type="link"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditPattern(item);
                                                }}
                                            >
                                                Edit
                                            </Button>
                                        ) : null,
                                        canDeletePattern ? (
                                            <Button
                                                key="delete"
                                                size="small"
                                                type="link"
                                                danger
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeletePatternInline(item);
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        ) : null
                                    ].filter(Boolean)}
                                    style={{
                                        cursor: 'pointer',
                                        background: selectedAreaPatternId === item.id ? '#e6f4ff' : undefined,
                                        borderRadius: 6,
                                        padding: '8px 12px',
                                    }}
                                >
                                    <List.Item.Meta
                                        title={item.archivedAt ? `${item.name} (Archived)` : item.name}
                                        description={
                                            <div className="pattern-list-meta">
                                                <div>
                                                    <div className="pattern-list-label">Type</div>
                                                    <div className="pattern-list-value">{item.patternType || 'custom'}</div>
                                                </div>
                                                <div>
                                                    <div className="pattern-list-label">Points</div>
                                                    <div className="pattern-list-value">{item.numPoints ?? 0}</div>
                                                </div>
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                            footer={
                                selectedAreaId && !loadingAreaPatterns ? (
                                    <div className="list-create-row">
                                        <Button
                                            type="text"
                                            icon={<PlusOutlined />}
                                            className="patterns-create-button list-create-button"
                                            onClick={handleCreatePattern}
                                            block
                                        >
                                            Create new pattern
                                        </Button>
                                    </div>
                                ) : null
                            }
                        />
                    </div>
                </Col>
                <Col xs={24} md={14}>
                    <div style={{ marginBottom: 8, fontWeight: 600 }}>Map View</div>
                    <div className="patterns-fallback-map" style={{ height: 600, border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                        {selectedAreaGeom ? (
                            <Map
                                ref={mapRef}
                                mapboxAccessToken={MAPBOX_TOKEN}
                                key={`${selectedAreaId}-${selectedAreaPatternId}`}
                                initialViewState={{ 
                                    longitude: selectedAreaGeom.coordinates[0][0][0],
                                    latitude: selectedAreaGeom.coordinates[0][0][1],
                                    zoom: 15
                                }}
                                style={{ width: '100%', height: '100%' }}
                                mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                                onLoad={() => {
                                    // Fit bounds when map loads
                                    if (selectedAreaGeom && mapRef.current) {
                                        const coords = selectedAreaGeom.coordinates[0];
                                        const lngs = coords.map((c: number[]) => c[0]);
                                        const lats = coords.map((c: number[]) => c[1]);
                                        const bounds: [[number, number], [number, number]] = [
                                            [Math.min(...lngs), Math.min(...lats)],
                                            [Math.max(...lngs), Math.max(...lats)]
                                        ];
                                        mapRef.current.fitBounds(bounds, { padding: 40, duration: 1000 });
                                    }
                                }}
                            >
                                {/* Area polygon */}
                                <Source
                                    id="area"
                                    type="geojson"
                                    data={{
                                        type: 'Feature',
                                        geometry: selectedAreaGeom,
                                        properties: {}
                                    }}
                                >
                                    <Layer
                                        id="area-fill"
                                        type="fill"
                                        paint={{ 'fill-color': '#2f54eb', 'fill-opacity': 0.1 }}
                                    />
                                    <Layer
                                        id="area-outline"
                                        type="line"
                                        paint={{ 'line-color': '#1d39c4', 'line-width': 2 }}
                                    />
                                </Source>

                                {/* Area vertices with black markers */}
                                {selectedAreaGeom.coordinates[0].map((coord: number[], idx: number) => (
                                    <Marker
                                        key={`vertex-${idx}`}
                                        longitude={coord[0]}
                                        latitude={coord[1]}
                                        anchor="center"
                                    >
                                        <div style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            background: '#000',
                                            border: '1px solid white',
                                        }} />
                                    </Marker>
                                ))}

                                {/* Pattern points with orange markers */}
                                {selectedAreaPatternPoints
                                    .filter(p => p.gpsLat != null && p.gpsLon != null)
                                    .sort((a, b) => (a.pointIndex || 0) - (b.pointIndex || 0))
                                    .map((p, idx) => (
                                        <Marker
                                            key={`point-${p.id}-${idx}`}
                                            longitude={p.gpsLon}
                                            latitude={p.gpsLat}
                                            anchor="center"
                                        >
                                            <div style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                background: '#fa8c16',
                                                border: '2px solid white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 10,
                                                fontWeight: 'bold',
                                                color: 'white',
                                            }}>
                                                {p.pointIndex}
                                            </div>
                                        </Marker>
                                    ))}
                            </Map>
                        ) : (
                            <div style={{ 
                                height: '100%', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                background: '#fafafa'
                            }}>
                                <Empty description={selectedAreaId ? 'Loading map...' : 'Select an area to view map'} />
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        );
    };

    return (
        <div className={`patterns-page ${shouldShowFallback ? 'patterns-mode-gps' : 'patterns-mode-template'}`}>
            <div className="patterns-header">
                <Title level={2} style={{ margin: 0 }}>Patterns</Title>
                <Checkbox checked={showArchived} onChange={e => setShowArchived(e.target.checked)}>Show archived patterns</Checkbox>
            </div>
            <Card className="patterns-main-card">
                <div className="patterns-filters" style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div className="patterns-area-type-control">
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Area type</div>
                            <Select
                                value={selectedAreaTypeId || undefined}
                                placeholder="Select area type"
                                style={{ minWidth: 240 }}
                                className="patterns-area-type-select"
                                onChange={(val: number) => { setSelectedAreaTypeId(val); setCreationMode(null); }}
                                options={areaTypes.map((t) => ({
                                    value: t.id,
                                    label: `${t.name}${t.maxVertices ? ` (${t.maxVertices} vertices)` : ''}`,
                                }))}
                            />
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Pattern coordinates</div>
                            <Select style={{ minWidth: 280 }} value={shouldShowFallback ? 'gps' : 'template'}
                                onChange={(value: 'gps' | 'template') => setCreationMode(value)}
                                options={[
                                    { value: 'gps', label: 'GPS points — one specific area' },
                                    { value: 'template', label: 'Normalized template — reusable (0–1)', disabled: !canUseTemplate },
                                ]} />
                        </div>
                    </div>
                </div>
                <Alert style={{ marginBottom: 16 }} showIcon type="info"
                    message={shouldShowFallback ? 'GPS pattern: fixed locations for one area' : 'Template pattern: reusable normalized coordinates'}
                    description={shouldShowFallback
                        ? 'Choose an area and place points on its GPS map. Points are stored as latitude/longitude. For areas without a template, or with a non-standard template image, the image is reference only; points are not normalized or reused across areas.'
                        : 'Place points on the standard four-corner template. xLocal/yLocal are stored from 0 to 1 and can be projected onto compatible areas of this type. Choose GPS points if you need fixed locations for one area.'} />
                {shouldShowFallback ? (
                    renderFallbackView()
                ) : (
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={8} className="patterns-list-panel">
                            <div style={{ marginBottom: 8, fontWeight: 600 }}>Patterns</div>
                            <List
                                loading={loadingPatterns}
                                dataSource={patterns}
                                locale={{ emptyText: <Empty description="No patterns" /> }}
                                className="patterns-scroll-list patterns-scroll-list--full"
                                renderItem={(item) => (
                                <List.Item
                                    className="patterns-list-item"
                                    onClick={() => setSelectedPatternId(item.id)}
                                    actions={[
                                        canEditPattern ? (
                                            <Button key="archive" size="small" type="link" onClick={e => { e.stopPropagation(); handleArchivePattern(item); }}>
                                                {item.archivedAt ? 'Restore' : 'Archive'}
                                            </Button>
                                        ) : null,
                                        canEditPattern ? (
                                            <Button
                                                key="edit"
                                                size="small"
                                                type="link"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditPattern(item);
                                                }}
                                            >
                                                Edit
                                            </Button>
                                        ) : null,
                                        canDeletePattern ? (
                                            <Button
                                                key="delete"
                                                size="small"
                                                type="link"
                                                danger
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeletePatternInline(item);
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        ) : null
                                    ].filter(Boolean)}
                                    style={{
                                        cursor: 'pointer',
                                        background: selectedPatternId === item.id ? '#e6f4ff' : undefined,
                                        borderRadius: 6,
                                            padding: '8px 12px',
                                        }}
                                    >
                                    <List.Item.Meta
                                        title={item.archivedAt ? `${item.name} (Archived)` : item.name}
                                        description={
                                            <div className="pattern-list-meta">
                                                <div>
                                                    <div className="pattern-list-label">Type</div>
                                                    <div className="pattern-list-value">{item.patternType || 'custom'}</div>
                                                </div>
                                                <div>
                                                    <div className="pattern-list-label">Points</div>
                                                    <div className="pattern-list-value">{item.numPoints ?? 0}</div>
                                                </div>
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                                footer={
                                    !loadingPatterns ? (
                                        <div className="list-create-row">
                                            <Button
                                                type="text"
                                                icon={<PlusOutlined />}
                                                className="patterns-create-button list-create-button"
                                                onClick={handleCreatePattern}
                                                block
                                            >
                                                Create new pattern
                                            </Button>
                                        </div>
                                    ) : null
                                }
                            />
                        </Col>
                        <Col xs={24} md={16} className="patterns-preview-panel">
                            <div style={{ marginBottom: 8, fontWeight: 600 }}>Preview</div>
                            <div className="patterns-preview-canvas">{renderPatternPreview()}</div>
                        </Col>
                    </Row>
                )}
            </Card>
        </div>
    );
};

export default Patterns;
